import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageProvider";
import { Markdown } from "./Markdown";
import { CODE_LANGUAGES, languageLabel } from "./CodeBlock";

export interface ComposerHandle {
  focus: () => void;
  insert: (text: string) => void;
}

type Mode = "write" | "preview" | "split";

const INDENT = "  ";

// Text area with a formatting toolbar, code blocks with a language picker,
// write/preview/side-by-side modes, Tab indentation, list continuation and
// Ctrl/Cmd+Enter to send.
export const Composer = forwardRef<
  ComposerHandle,
  {
    value: string;
    onChange: (v: string) => void;
    onSubmit?: () => void;
    placeholder?: string;
    minRows?: number;
    disabled?: boolean;
  }
>(({ value, onChange, onSubmit, placeholder, minRows = 4, disabled }, ref) => {
  const { t } = useLanguage();
  const area = useRef<HTMLTextAreaElement>(null);
  const [mode, setMode] = useState<Mode>("write");
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!langOpen) return;
    const close = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [langOpen]);

  // Replace [a, b) with `text` and place the selection at [selA, selB].
  const splice = (a: number, b: number, text: string, selA: number, selB = selA) => {
    onChange(value.slice(0, a) + text + value.slice(b));
    requestAnimationFrame(() => {
      const el = area.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(selA, selB);
    });
  };

  const wrap = (before: string, after = before, fallback = "") => {
    const el = area.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const sel = value.slice(a, b) || fallback;
    splice(a, b, before + sel + after, a + before.length, a + before.length + sel.length);
  };

  // Prefix every selected line (quote, list).
  const prefixLines = (prefix: string) => {
    const el = area.current;
    if (!el) return;
    const a = value.lastIndexOf("\n", el.selectionStart - 1) + 1;
    const b = el.selectionEnd;
    const block = value.slice(a, b).split("\n").map((l) => prefix + l).join("\n");
    splice(a, b, block, a + block.length);
  };

  const codeBlock = (lang: string) => {
    setLangOpen(false);
    const el = area.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b } = el;
    const sel = value.slice(a, b);
    const lead = a > 0 && value[a - 1] !== "\n" ? "\n" : "";
    const open = `${lead}\`\`\`${lang}\n`;
    const text = `${open}${sel}\n\`\`\`\n`;
    splice(a, b, text, a + open.length, a + open.length + sel.length);
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (mode === "preview") setMode("write");
      requestAnimationFrame(() => area.current?.focus());
    },
    insert: (text: string) => {
      if (mode === "preview") setMode("write");
      onChange(value ? `${value.replace(/\s*$/, "")}\n\n${text}` : text);
      requestAnimationFrame(() => {
        const el = area.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      });
    },
  }));

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    const { selectionStart: a, selectionEnd: b } = el;
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      if (onSubmit) {
        e.preventDefault();
        onSubmit();
      }
      return;
    }
    if (e.key === "Tab") {
      // Indent/outdent; inside the editor Tab never leaves the field.
      e.preventDefault();
      const lineStart = value.lastIndexOf("\n", a - 1) + 1;
      if (a === b && !e.shiftKey) {
        splice(a, b, INDENT, a + INDENT.length);
        return;
      }
      const block = value.slice(lineStart, b);
      const lines = block.split("\n");
      const next = e.shiftKey
        ? lines.map((l) => l.replace(new RegExp(`^ {1,${INDENT.length}}`), ""))
        : lines.map((l) => INDENT + l);
      const text = next.join("\n");
      splice(lineStart, b, text, lineStart, lineStart + text.length);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && a === b) {
      // Continue lists and quotes; an empty item ends the list.
      const lineStart = value.lastIndexOf("\n", a - 1) + 1;
      const line = value.slice(lineStart, a);
      const m = /^(\s*)([-*•]|\d+[.)]|>)\s+(.*)$/.exec(line);
      if (m) {
        e.preventDefault();
        if (!m[3].trim()) {
          splice(lineStart, a, "", lineStart);
          return;
        }
        const marker = /^\d+/.test(m[2]) ? `${parseInt(m[2], 10) + 1}${m[2].slice(-1)}` : m[2];
        const ins = `\n${m[1]}${marker} `;
        splice(a, b, ins, a + ins.length);
        return;
      }
      // Keep the indentation inside code.
      const indent = /^(\s+)/.exec(line)?.[1];
      if (indent) {
        e.preventDefault();
        splice(a, b, `\n${indent}`, a + 1 + indent.length);
      }
    }
  };

  const rows = Math.min(Math.max(minRows, value.split("\n").length + 1), mode === "split" ? 26 : 22);

  return (
    <div className={`fm-composer-box ${disabled ? "is-disabled" : ""} ${mode === "split" ? "is-split" : ""}`}>
      <div className="fm-composer-bar">
        <div className="fm-tabs-mini">
          {(["write", "preview", "split"] as Mode[]).map((m) => (
            <button key={m} type="button" className={`${mode === m ? "is-active" : ""} ${m === "split" ? "fm-hide-sm" : ""}`} onClick={() => setMode(m)}>
              {t(`forum.mode.${m}`)}
            </button>
          ))}
        </div>
        {mode !== "preview" && (
          <div className="fm-tools">
            <button type="button" title={t("forum.fmt.bold")} onClick={() => wrap("**", "**", t("forum.fmt.boldText"))}>
              <b>B</b>
            </button>
            <button type="button" title={t("forum.fmt.italic")} onClick={() => wrap("*", "*", t("forum.fmt.italicText"))}>
              <i>I</i>
            </button>
            <button type="button" title={t("forum.fmt.quote")} onClick={() => prefixLines("> ")}>
              ❝
            </button>
            <button type="button" title={t("forum.fmt.list")} onClick={() => prefixLines("- ")}>
              ≡
            </button>
            <button type="button" title={t("forum.fmt.link")} onClick={() => wrap("[", "](https://)", t("forum.fmt.linkText"))}>
              🔗
            </button>
            <span className="fm-tools__sep" />
            <button type="button" title={t("forum.fmt.code")} onClick={() => wrap("`", "`", "kod")}>
              <code>`</code>
            </button>
            <div className="fm-lang-wrap" ref={langRef}>
              <button
                type="button"
                className="fm-tools__code"
                title={t("forum.fmt.codeBlock")}
                aria-expanded={langOpen}
                onClick={() => setLangOpen((o) => !o)}
              >
                {"{ }"} <span className="fm-hide-sm">{t("forum.fmt.codeBlock")}</span> ▾
              </button>
              {langOpen && (
                <div className="fm-menu fm-lang-menu">
                  {CODE_LANGUAGES.map((l) => (
                    <button key={l} type="button" className="fm-menu-item" onClick={() => codeBlock(l)}>
                      <span className="fm-lang-dot" data-lang={l} />
                      {languageLabel(l)}
                    </button>
                  ))}
                  <button type="button" className="fm-menu-item" onClick={() => codeBlock("")}>
                    <span className="fm-lang-dot" />
                    {t("forum.fmt.plainCode")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="fm-composer-body">
        {mode !== "preview" && (
          <textarea
            ref={area}
            className="fm-textarea"
            value={value}
            rows={rows}
            placeholder={placeholder}
            maxLength={10000}
            disabled={disabled}
            spellCheck
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
          />
        )}
        {mode !== "write" && (
          <div className="fm-preview">
            {value.trim() ? <Markdown text={value} /> : <span className="fm-muted">{t("forum.previewEmpty")}</span>}
          </div>
        )}
      </div>
      <div className="fm-composer-hint">
        <span>{t("forum.fmt.hint")}</span>
        <span className={value.length > 9000 ? "is-warn" : ""}>{value.length.toLocaleString()} / 10 000</span>
      </div>
    </div>
  );
});
